import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {NucleotideType} from 'biojs/BioConstants';
import * as CRISPRConstants from 'constants/CRISPRConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import FolderSelector from 'file-browser/FolderSelector';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {filterTagSchemasGraphQL} from 'tag-schema/filterTagSchemas';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {ButtonSize} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';
import {GenericEvent} from 'util/Types';
import AddToWorklistForm, {useAddToWorklistForm, WorklistFormSelectorType} from 'worklists/AddToWorklistForm';
import {WorklistType} from 'worklists/WorklistConstants';

export interface BasicSequencePrimer {
  primer_api_id: string;
}

export interface GuidePrimerModalProps {
  numSelectedGuides: number;
  designType: CRISPRConstants.DESIGN_TYPE;
  onRequestClose: () => void;
  onRequestSubmit: (success: boolean) => void;
  saveGuidePrimers: (
    folderId: string,
    nucleotideType: NucleotideType,
    tagSchemaId: string | undefined
  ) => JQueryPromise<{primers: Array<BasicSequencePrimer>}>;
  hasRNAOligoSchemas?: boolean;
  hasDNAOligoSchemas?: boolean;
}

export const GuidePrimerModal = (props: GuidePrimerModalProps): JSX.Element => {
  const {numSelectedGuides, onRequestClose, onRequestSubmit, hasRNAOligoSchemas, hasDNAOligoSchemas} = props;
  const [guidePrimerFolderId, setGuidePrimerFolderId] = useState<string | null>(null);
  const [nucleotideType, setNucleotideType] = useState(NucleotideType.DNA);
  const [tagSchemaId, setTagSchemaId] = useState<string | undefined>(undefined);

  const worklistFormSettings = {
    autoFocus: false,
    ids: [],
    refreshOptions: {},
    worklistType: WorklistType.BIOENTITY,
  };
  const worklistFormState = useAddToWorklistForm(worklistFormSettings);
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSubmit = async () => {
    const saveResponse = await Promise.resolve(
      props.saveGuidePrimers(guidePrimerFolderId!, nucleotideType, tagSchemaId)
    );
    if (saveResponse && !worklistFormState.isDisabled) {
      worklistFormState.addItems(saveResponse.primers.map((p: BasicSequencePrimer) => p.primer_api_id));
    }
  };
  const cannotBeSavedAsRNA = props.designType === CRISPRConstants.DESIGN_TYPE.PAIRED;

  const handleChangeNucleotideType = (newNucleotideType: NucleotideType): void => {
    setNucleotideType(newNucleotideType);
    if (newNucleotideType !== nucleotideType) {
      setTagSchemaId(undefined);
    }
  };

  return (
    <Modal
      title={`Save ${numSelectedGuides} ${benchlingUtil.pluralize(numSelectedGuides, 'Guide')} as
        ${benchlingUtil.pluralize(numSelectedGuides, 'Oligo')}`}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        handleSubmit();
        onRequestSubmit(true);
      }}
      buttons={[{text: 'Save', submit: true, disabled: !guidePrimerFolderId}]}
    >
      <FormGroup>
        <Label required={true}>Location</Label>
        <FolderSelector
          enableRedesign={true}
          showInventory={true}
          value={guidePrimerFolderId}
          onChange={(e: GenericEvent) => {
            setGuidePrimerFolderId(e.target.value);
          }}
          unsetOnFetchFail={true}
        />
      </FormGroup>
      <FormGroup>
        <Label required={true}>Nucleotide Type</Label>
        {cannotBeSavedAsRNA ? <p>Primer pairs can only be saved as DNA Oligos</p> : undefined}
        <ToggleGroup
          options={[
            {id: NucleotideType.DNA, buttonProps: {text: 'DNA'}},
            {id: NucleotideType.RNA, buttonProps: {text: 'RNA'}},
          ]}
          disabled={cannotBeSavedAsRNA}
          selection={nucleotideType}
          onChangeSelection={handleChangeNucleotideType}
          size={ButtonSize.SMALL}
          isFullWidth={true}
        />
      </FormGroup>
      {((nucleotideType === NucleotideType.RNA && hasRNAOligoSchemas) ||
        (nucleotideType === NucleotideType.DNA && hasDNAOligoSchemas)) && (
        <FormGroup>
          <Label>Schema</Label>
          <TagSchemaSelector
            allowNone={true}
            oligoAwareFolderItemTypes={
              nucleotideType === NucleotideType.DNA
                ? [ModelTypeConstants.DNA_OLIGO]
                : [ModelTypeConstants.RNA_OLIGO]
            }
            onChange={(tagSchema) => {
              setTagSchemaId(tagSchema ? tagSchema.id : undefined);
            }}
            selectedTagSchemaId={tagSchemaId}
          />
        </FormGroup>
      )}
      {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && (
        <FormGroup>
          <Label>Add to Worklist (optional)</Label>
          <AddToWorklistForm
            {...worklistFormState}
            {...worklistFormSettings}
            selectorType={WorklistFormSelectorType.TOGGLE_GROUP}
          />
        </FormGroup>
      )}
    </Modal>
  );
};

function QueryingGuidePrimerModal(props: GuidePrimerModalProps): JSX.Element {
  return (
    <BenchlingQuery<AllTagSchemasQuery> query={ALL_TAG_SCHEMAS_QUERY} renderLoading={() => null}>
      {(data) => {
        function hasOligoSchemas(folderItemTypes: Array<OligoAwareFolderItemType>): boolean {
          return (
            filterTagSchemasGraphQL(data.tagSchemas, {
              oligoAwareFolderItemTypes: folderItemTypes,
            }).length > 0
          );
        }

        return (
          <GuidePrimerModal
            {...props}
            hasRNAOligoSchemas={hasOligoSchemas([ModelTypeConstants.RNA_OLIGO])}
            hasDNAOligoSchemas={hasOligoSchemas([ModelTypeConstants.DNA_OLIGO])}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export default QueryingGuidePrimerModal;
